import { Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { ColumnDef } from '../common/tableFeatures';
import { clipped, dateSortValue, dateTime } from '../common/tableCells';
import { StatusChip } from '../common/StatusChip';
import type { DeploymentSummary, DeploymentTarget } from '@store/api/deploymentsApi';
import { commandResultSummary, targetStatus } from './labels';

/** Columns for a list of Deployments; the version links to the progress page. */
export const deploymentSummaryColumns: ColumnDef<DeploymentSummary>[] = [
  {
    id: 'created_at',
    accessorFn: (row) => dateSortValue(row.created_at),
    header: 'Created',
    cell: ({ row }) => dateTime(row.original.created_at),
    size: 170,
  },
  {
    id: 'version',
    accessorKey: 'version',
    header: 'Version',
    cell: ({ row }) => (
      <Link component={RouterLink} to={`/deployments/${row.original.id}`} sx={{
        fontWeight: 600
      }}>
        {row.original.version}
      </Link>
    ),
    size: 120,
  },
  { id: 'created_by', accessorKey: 'created_by', header: 'By', size: 120 },
  { id: 'total', accessorFn: (row) => row.counts.total, header: 'Devices', size: 80 },
  { id: 'succeeded', accessorFn: (row) => row.counts.succeeded, header: 'Succeeded', size: 90 },
  { id: 'failed', accessorFn: (row) => row.counts.failed, header: 'Failed', size: 80 },
  { id: 'in_flight', accessorFn: (row) => row.counts.in_flight, header: 'In progress', size: 90 },
];

/** Columns for the per-Device progress of one Deployment. */
export const deploymentTargetColumns: ColumnDef<DeploymentTarget>[] = [
  {
    id: 'device',
    accessorFn: (row) => row.device_name || row.device_id,
    header: 'Device',
    cell: ({ row }) => (
      <Link component={RouterLink} to={`/devices/${row.original.device_id}`}>
        {row.original.device_name || row.original.device_id}
      </Link>
    ),
    size: 220,
  },
  {
    id: 'status',
    accessorFn: targetStatus,
    header: 'Status',
    cell: ({ row }) => <StatusChip status={targetStatus(row.original)} type="deployment" />,
    size: 120,
  },
  {
    id: 'previous_version',
    accessorKey: 'previous_version',
    header: 'Previous version',
    cell: ({ row }) => row.original.previous_version || '-',
    size: 130,
  },
  {
    id: 'result',
    accessorFn: (row) => commandResultSummary(row.command_result),
    header: 'Result',
    enableSorting: false,
    size: 200,
  },
  {
    id: 'completed_at',
    accessorFn: (row) => dateSortValue(row.completed_at),
    header: 'Completed',
    cell: ({ row }) => dateTime(row.original.completed_at),
    size: 170,
  },
  {
    id: 'error',
    accessorKey: 'error',
    header: 'Error',
    cell: ({ row }) =>
      row.original.error ? (
        clipped(row.original.error, 360, { error: true })
      ) : (
        <Typography variant="body2" sx={{
          color: 'text.secondary'
        }}>
          -
        </Typography>
      ),
    enableSorting: false,
    size: 360,
  },
];
